import { Fragment, useCallback, useEffect, useState } from 'react';
import { Badge, Button, Form, Spinner, Table } from 'react-bootstrap';
import axios from 'axios';
import { toast } from 'react-toastify';

import { formatDateTime } from '../utils/dateFormat';
/**
 * System Admin: what account managers asked the help assistant. The questions the help text did not cover, or that the person
 * marked not helpful, are what the help is missing: write them into the help (helpContent.js) and regenerate the assistant's text.
 * Questions it refused as abusive or not about iBigDo are listed apart, with who asked.
 */
function AssistantQuestionsAdmin() {
  const [view, setView] = useState('missed');
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get('/api/system-admin/assistant-questions', { params: { view, limit: 200 } });
      setRows(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error('Could not load the questions');
      setRows([]);
    }
  }, [view]);

  useEffect(() => { setRows(null); load(); }, [load]);

  if (rows === null) return <div className="d-flex justify-content-center py-5"><Spinner animation="border" role="status" /></div>;

  return (
    <div>
      <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
        <Form.Select size="sm" style={{ width: 'auto' }} value={view} onChange={(e) => setView(e.target.value)} aria-label="Which questions">
          <option value="missed">What the help missed (not covered, or not helpful)</option>
          <option value="flagged">Refused: abusive or not about iBigDo</option>
          <option value="all">Everything, latest first</option>
        </Form.Select>
        <Button size="sm" variant="outline-secondary" onClick={load}>Refresh</Button>
      </div>
      {rows.length === 0 ? (
        <p className="text-muted">{view === 'missed' ? 'Nothing the help missed yet.' : view === 'flagged' ? 'No questions refused.' : 'No questions yet.'}</p>
      ) : (
        <Table size="sm" hover responsive>
          <thead>
            <tr><th>When</th><th>Who</th><th>Question</th><th>Help</th><th>Cost</th></tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <Fragment key={r.id}>
                <tr style={{ cursor: 'pointer' }} onClick={() => setOpen(open === r.id ? null : r.id)}>
                  <td className="text-nowrap">{r.askedAt ? formatDateTime(r.askedAt) : ''}</td>
                  <td>
                    {r.email}
                    <div className="small text-muted">
                      {r.audience === 'CUSTOMER' ? `Customer${r.organizationId ? `, org ${r.organizationId}` : ''}` : 'Account manager'}
                    </div>
                  </td>
                  <td>{r.question}</td>
                  <td className="text-nowrap">
                    {r.error ? <Badge bg="danger">Failed</Badge>
                      : r.flag === 'ABUSIVE' ? <Badge bg="danger">Abusive</Badge>
                        : r.flag === 'OFF_TOPIC' ? <Badge bg="secondary">Off topic</Badge>
                          : r.covered === false ? <Badge bg="warning" text="dark">Not covered</Badge>
                            : <Badge bg="success">Covered</Badge>}
                    {r.helpful === false && <Badge bg="danger" className="ms-1">Not helpful</Badge>}
                    {r.helpful === true && <Badge bg="success" className="ms-1">Helpful</Badge>}
                  </td>
                  <td className="text-nowrap small text-muted">
                    {r.inputTokens != null ? `${r.inputTokens} in (${r.cacheReadTokens || 0} cached) / ${r.outputTokens} out` : ''}
                  </td>
                </tr>
                {open === r.id && (
                  <tr>
                    <td colSpan={5} className="bg-light">
                      {r.error ? <span className="text-danger">{r.error}</span> : <div style={{ whiteSpace: 'pre-wrap' }}>{r.answer}</div>}
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </Table>
      )}
    </div>
  );
}

export default AssistantQuestionsAdmin;
